import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react';

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'theme';

type ThemeContextValue = {
    theme: Theme;
    setTheme: (theme: Theme) => void;
    toggle: () => void;
};

const ThemeContext = createContext<ThemeContextValue>({
    theme: 'light',
    setTheme: () => {},
    toggle: () => {}
});

const readInitialTheme = (): Theme => {
    try {
        const stored = window.localStorage.getItem(STORAGE_KEY);
        if (stored === 'light' || stored === 'dark') {
            return stored;
        }
    } catch {
        // Storage unavailable (private mode); fall through to the OS preference.
    }

    if (
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-color-scheme: dark)').matches
    ) {
        return 'dark';
    }

    return 'light';
};

/**
 * Light/dark theme. Applied as data-bs-theme on <html> so both Bootstrap's own
 * colour modes and the --ft-* token overrides in _tokens.scss switch together.
 * The choice persists in localStorage; the first visit follows the OS setting.
 */
const ThemeProvider = ({children}: {children: ReactNode}) => {
    const [theme, setTheme] = useState<Theme>(readInitialTheme);

    useEffect(() => {
        document.documentElement.setAttribute('data-bs-theme', theme);
        try {
            window.localStorage.setItem(STORAGE_KEY, theme);
        } catch {
            // ignore: the theme simply will not persist
        }
    }, [theme]);

    const toggle = useCallback(
        () => setTheme(current => (current === 'dark' ? 'light' : 'dark')),
        []
    );

    const value = useMemo(() => ({theme, setTheme, toggle}), [theme, toggle]);

    return (
        <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
    );
};

const useTheme = () => useContext(ThemeContext);

// eslint-disable-next-line react-refresh/only-export-components
export {ThemeProvider, useTheme};
